import { ManagementCompanyStatus } from "@/modules/common/types/ManagementCompanies.enum";
import { Pagination } from "@/modules/common/types/Pagination";
import api from "../api";
import {
  CreateAdminManagementCompanyDto,
  ListAdminManagementCompaniesDto,
  ListAdminManagementCompaniesManagerUsersDto,
  UpdateAdminManagementCompanyDto,
} from "../dto/admin-management-companies";
import {
  AdminManagementCompaniesTotalizersResponse,
  AdminManagementCompanyOwnersResponse,
  AdminManagementCompanyResponse,
  AdminManagementCompanyManagerUserDetailResponse,
  AdminManagementCompanyManagerUserResponse,
  CreatedManagementCompanyResponse,
  DemoTemplateModuleResponse,
  UpdateManagementCompanyResponse,
} from "../response/admin-management-companies";

export const ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS = {
  GET_TOTALIZERS: "/v1/admin/management-companies/totalizers",
  GET_OWNERS: "/v1/admin/management-companies/owners",
  GET_MANAGEMENT_COMPANIES: "/v1/admin/management-companies",
  CREATE_MANAGEMENT_COMPANY: "/v1/admin/management-companies",
  GET_MANAGEMENT_COMPANY: (id: string) => `/v1/admin/management-companies/${id}`,
  EDIT_MANAGEMENT_COMPANY: (id: string) => `/v1/admin/management-companies/${id}`,
  GET_MANAGER_USERS: (id: string) => `/v1/admin/management-companies/${id}/manager-users`,
  GET_MANAGER_USER: (id: string, managerUserId: string) => `/v1/admin/management-companies/${id}/manager-users/${managerUserId}`,
  GET_DEMO_TEMPLATE_MODULES: "/v1/admin/management-companies/demo-template-modules",
};

export const AdminManagementCompaniesService = {
  getTotalizers: async (): Promise<AdminManagementCompaniesTotalizersResponse> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_TOTALIZERS);
    return response.data;
  },

  getOwners: async (): Promise<AdminManagementCompanyOwnersResponse> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_OWNERS);
    return response.data;
  },

  getManagerUsers: async (
    id: string,
    params?: ListAdminManagementCompaniesManagerUsersDto,
  ): Promise<Pagination<AdminManagementCompanyManagerUserResponse>> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_MANAGER_USERS(id), { params });
    return response.data;
  },

  getManagerUser: async (id: string, managerUserId: string): Promise<AdminManagementCompanyManagerUserDetailResponse> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_MANAGER_USER(id, managerUserId));
    return response.data;
  },

  getManagementCompanies: async ({
    status,
    ...params
  }: ListAdminManagementCompaniesDto): Promise<Pagination<AdminManagementCompanyResponse>> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_MANAGEMENT_COMPANIES, {
      // The API reads `status` as a comma separated list; axios would serialize an array as `status[]`.
      params: { ...params, status: status?.length ? status.join(",") : undefined },
    });
    return response.data;
  },

  getManagementCompany: async (id: string): Promise<AdminManagementCompanyResponse> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_MANAGEMENT_COMPANY(id));
    return response.data;
  },

  createManagementCompany: async (data: CreateAdminManagementCompanyDto): Promise<CreatedManagementCompanyResponse> => {
    const response = await api.post(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.CREATE_MANAGEMENT_COMPANY, data);
    return response.data;
  },

  updateManagementCompany: async (id: string, data: UpdateAdminManagementCompanyDto): Promise<UpdateManagementCompanyResponse> => {
    const response = await api.put(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.EDIT_MANAGEMENT_COMPANY(id), data);
    return response.data;
  },

  inactivateManagementCompany: async (id: string): Promise<UpdateManagementCompanyResponse> => {
    const response = await api.put(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.EDIT_MANAGEMENT_COMPANY(id), {
      status: ManagementCompanyStatus.INACTIVE,
    });
    return response.data;
  },

  getDemoTemplateModules: async (): Promise<DemoTemplateModuleResponse[]> => {
    const response = await api.get(ADMIN_MANAGEMENT_COMPANIES_ENDPOINTS.GET_DEMO_TEMPLATE_MODULES);
    return response.data;
  },
};
